import { useArtifactViewCtx } from "@/components/artifacts/view/ArtifactViewContext.tsx";
import { CalendarAvailabilityPicker } from "@/components/scheduling/CalendarAvailabilityPicker.tsx";
import { Button } from "@/components/ui/button.tsx";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover.tsx";
import { DurationPicker } from "@/components/typeahead/duration-picker.tsx";
import { LocationPicker } from "@/components/typeahead/location-picker.tsx";
import { TimeDatePicker } from "@/components/typeahead/time-date-picker.tsx";
import { TitleInput } from "@/components/typeahead/title-input.tsx";
import { parseISOToDate } from "@/lib/date-utils.ts";
import type { ArtifactComponent } from "@/types/artifacts-view.ts";
import { useState } from "react";
import type { ReactNode } from "react";

type SchedulingMadlibsComponentData = Extract<ArtifactComponent, { type: "schedulingMadlibs" }>;

/** Convert minutes to the duration picker's string format. */
const minutesToDurationString = (minutes: number): string => {
  if (minutes < 60) return `${minutes} min`;
  const hours = minutes / 60;
  if (hours === 1) return "1 hour";
  return `${hours} hours`;
};

const parseDurationString = (str: string): number => {
  const trimmed = str.trim().toLowerCase();
  const hourMatch = trimmed.match(/^([\d.]+)\s*hours?$/);
  if (hourMatch?.[1]) return Math.round(Number.parseFloat(hourMatch[1]) * 60);
  const minMatch = trimmed.match(/^(\d+)\s*min(utes?)?$/);
  if (minMatch?.[1]) return Number.parseInt(minMatch[1], 10);
  const num = Number.parseInt(trimmed, 10);
  return Number.isNaN(num) ? 30 : num;
};

const DAYS_OF_WEEK = new Set([
  "monday",
  "tuesday",
  "wednesday",
  "thursday",
  "friday",
  "saturday",
  "sunday",
]);
const SIMPLE_TIME_PHRASES = new Set(["today", "tomorrow", "this week", "next week"]);

/** Connector word for the time-range prose ("on Tuesday" / "within the next week"). */
const timeConnector = (text: string): string => {
  const lower = text.trim().toLowerCase();
  if (DAYS_OF_WEEK.has(lower) || Array.from(DAYS_OF_WEEK).some((d) => lower.startsWith(d)))
    return " on ";
  if (SIMPLE_TIME_PHRASES.has(lower)) return " ";
  return " within ";
};

const EditableField = ({
  value,
  label,
  isEditable,
  children,
  onClick,
}: {
  value: string;
  label: string;
  isEditable: boolean;
  children: (props: { onClose: () => void }) => ReactNode;
  /** When provided, clicking the field calls this instead of opening the popover. */
  onClick?: () => void;
}) => {
  const [open, setOpen] = useState(false);

  if (!isEditable) return <span className="font-semibold">{value}</span>;

  // Inline madlib trigger styled as dashed-underline prose text. `variant="blank"`
  // + `size="fit"` strip the Button's chrome (height/padding/fill) so it sits
  // inline in the sentence while still routing through the shared component.
  const triggerClassName =
    "font-semibold border-b border-dashed border-muted-foreground/40 hover:border-primary focus:border-primary";
  const fieldSlug = label
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-|-$/g, "");
  const statsigName = `scheduling-madlib-${fieldSlug}`;

  if (onClick) {
    return (
      <Button
        variant="blank"
        size="fit"
        className={triggerClassName}
        onClick={onClick}
        data-statsig-name={statsigName}
      >
        {value}
      </Button>
    );
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="blank"
          size="fit"
          className={triggerClassName}
          data-statsig-name={statsigName}
        >
          {value}
        </Button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        className="w-72 p-0"
        onOpenAutoFocus={(e) => e.preventDefault()}
      >
        <div className="px-3 pt-2 pb-1 text-xs font-medium text-muted-foreground">{label}</div>
        {children({ onClose: () => setOpen(false) })}
      </PopoverContent>
    </Popover>
  );
};

export const SchedulingMadlibsBlock = ({
  component,
}: {
  component: SchedulingMadlibsComponentData;
}) => {
  const { isReadOnly, patch, patchOne } = useArtifactViewCtx();
  const isEditable = !isReadOnly && component.editable;
  const [availabilityPickerOpen, setAvailabilityPickerOpen] = useState(false);

  const locationDisplay =
    component.location ||
    (component.virtualMeetingType
      ? component.virtualMeetingType === "googleMeet"
        ? "Google Meet"
        : component.virtualMeetingType === "zoom"
          ? "Zoom"
          : component.virtualMeetingType === "teams"
            ? "Teams"
            : component.virtualMeetingType
      : null);

  return (
    <>
      <div className="text-xs leading-relaxed text-muted-foreground">
        {"Schedule a meeting"}
        {timeConnector(
          component.userConstraintRanges ? "custom times" : component.roughTimeRange.text,
        )}
        <EditableField
          value={
            component.userConstraintRanges
              ? `${component.userConstraintRanges.ranges.length} selected ${
                  component.userConstraintRanges.ranges.length === 1 ? "slot" : "slots"
                }`
              : component.roughTimeRange.text.toLowerCase()
          }
          label="When?"
          isEditable={isEditable}
          onClick={
            component.userConstraintRanges ? () => setAvailabilityPickerOpen(true) : undefined
          }
        >
          {({ onClose }) => (
            <TimeDatePicker
              defaultValue={component.roughTimeRange.text}
              onSelect={(timeDate) => {
                void patch([
                  {
                    path: component.roughTimeRangeEditPath,
                    value: { ...component.roughTimeRange, text: timeDate },
                  },
                  { path: component.userConstraintRangesEditPath, value: null },
                ]);
                onClose();
              }}
              onPickSpecificTimes={() => {
                onClose();
                setAvailabilityPickerOpen(true);
              }}
            />
          )}
        </EditableField>
        {" for "}
        <EditableField
          value={minutesToDurationString(component.duration)}
          label="How long?"
          isEditable={isEditable}
        >
          {({ onClose }) => (
            <DurationPicker
              defaultValue={minutesToDurationString(component.duration)}
              onSelect={(dur) => {
                void patchOne(component.durationEditPath, parseDurationString(dur));
                onClose();
              }}
            />
          )}
        </EditableField>
        {!component.isResponder && locationDisplay ? (
          <>
            {component.location ? " at " : ", "}
            <EditableField
              value={component.location ? component.location : "virtually"}
              label="Where?"
              isEditable={isEditable}
            >
              {({ onClose }) => (
                <LocationPicker
                  defaultValue={
                    component.location
                      ? { type: "in-person", details: component.location }
                      : { type: "virtual" }
                  }
                  onSelect={(loc) => {
                    if (loc.type === "virtual") {
                      void patch([
                        { path: component.locationEditPath, value: "" },
                        {
                          path: component.virtualMeetingTypeEditPath,
                          value: component.virtualMeetingType ?? "googleMeet",
                        },
                      ]);
                      onClose();
                    } else if (loc.details) {
                      void patchOne(component.locationEditPath, loc.details);
                      onClose();
                    }
                    // In-person without details: keep popover open for address input
                  }}
                />
              )}
            </EditableField>
          </>
        ) : null}
        {!component.isResponder && component.subject ? (
          <>
            {', titled "'}
            <EditableField value={component.subject} label="Title" isEditable={isEditable}>
              {({ onClose }) => (
                <TitleInput
                  defaultValue={component.subject}
                  onSubmit={(title) => {
                    if (title) void patchOne(component.subjectEditPath, title);
                    onClose();
                  }}
                />
              )}
            </EditableField>
            {'"'}
          </>
        ) : null}
      </div>

      <CalendarAvailabilityPicker
        open={availabilityPickerOpen}
        onOpenChange={setAvailabilityPickerOpen}
        initialDate={parseISOToDate(component.roughTimeRange.startISO)}
        duration={component.duration}
        initialRanges={
          component.userConstraintRanges?.ranges ?? component.defaultAvailabilityRanges
        }
        initialSkipCalendarConflicts={component.skipCalendarConflicts}
        onSave={(ranges, skipCalendarConflicts) => {
          if (ranges.length === 0) {
            void patch([
              { path: component.userConstraintRangesEditPath, value: null },
              { path: component.skipCalendarConflictsEditPath, value: skipCalendarConflicts },
            ]);
          } else {
            void patch([
              { path: component.userConstraintRangesEditPath, value: { ranges } },
              { path: component.skipCalendarConflictsEditPath, value: skipCalendarConflicts },
            ]);
          }
        }}
      />
    </>
  );
};
